/**
 * File contains hook functions that support various operations on
 * puzzle documents and collection.
 */

import {
  useFirestoreCollectionMutation,
  useFirestoreDocument,
  useFirestoreDocumentDeletion,
  useFirestoreDocumentMutation,
  useFirestoreQuery,
} from '@react-query-firebase/firestore';
import {
  collection,
  query,
  where,
  doc,
  orderBy,
  limit,
  startAfter,
  getDoc,
} from 'firebase/firestore';

import { PuzzleStatus } from '../../types';
import { firestore } from '../Firebase';

export const getAllPuzzles = () => {
  const ref = query(collection(firestore, 'puzzles'));
  return useFirestoreQuery(['allPuzzles'], ref, {
    subscribe: true,
    includeMetadataChanges: true,
  });
};

export const getAllPuzzlesApproved = () => {
  const ref = query(
    collection(firestore, 'puzzles'),
    where('status', '==', PuzzleStatus.APPROVED),
    orderBy('createdAt', 'asc'),
  );
  return useFirestoreQuery(['allPuzzlesApproved'], ref, {
    subscribe: true,
    includeMetadataChanges: true,
  });
};

export const getAllPuzzlesByCollectionId = (id: string) => {
  const puzzleRef = query(collection(firestore, 'puzzles'), where('collectionId', '==', id));
  return useFirestoreQuery(['puzzlesByCollectionId'], puzzleRef, {
    subscribe: true,
  });
};

export const puzzlesForApprove = () => {
  const ref = query(
    collection(firestore, 'puzzles'),
    where('status', '==', PuzzleStatus.PENDING_UPLOAD),
  );
  return useFirestoreQuery(['puzzlesForApprove'], ref, {
    subscribe: true,
  });
};

export const puzzlesSortByCreatedDate = () => {
  const ref = query(
    collection(firestore, 'puzzles'),
    where('status', '==', PuzzleStatus.APPROVED),
    orderBy('createdAt', 'desc'),
    limit(10),
  );
  return useFirestoreQuery(['puzzlesOrderByCollectionId'], ref);
};

export const puzzlesNext = (last: any) => {
  const ref = query(
    collection(firestore, 'puzzles'),
    where('status', '==', PuzzleStatus.APPROVED),
    orderBy('createdAt', 'desc'),
    startAfter(last),
    limit(5),
  );
  return useFirestoreQuery(['pagination'], ref);
};

export const updatePuzzle = (id: string) => {
  const ref = doc(collection(firestore, 'puzzles'), id);
  return useFirestoreDocumentMutation(ref, { merge: true });
};

export const deletePuzzle = (id: string) => {
  const ref = doc(collection(firestore, 'puzzles'), id);
  return useFirestoreDocumentDeletion(ref);
};

export const addPuzzle = () => {
  const ref = collection(firestore, 'puzzles');
  return useFirestoreCollectionMutation(ref);
};

export const getPuzzlesById = (id: string) => {
  const ref = doc(firestore, 'puzzles', id);
  return useFirestoreDocument(['puzzlesById', id], ref, {
    subscribe: true,
  });
};

export const rawGetPuzzlesById = async (id: string) => {
  const docRef = doc(firestore, 'puzzles', id);
  const docSnap = await getDoc(docRef);
  return docSnap;
};

export const getPuzzlesByUser = (id: string) => {
  const ref = query(collection(firestore, 'puzzles'), where('Publisher', '==', id));
  return useFirestoreQuery([`puzzlesByPublisher.${id}`], ref, {
    subscribe: true,
  });
};
